import { useMutation, useQueryClient } from '@tanstack/react-query';

import { UserProfileApiInstance } from '~src/main/api';

import { useShipmentTemplateQueryKey } from './queries';
import { TemplateUpsert } from './types';

const templateUpsertToAPIUpsert = (template: TemplateUpsert) => ({
  ...template,
  address_from: template.contact_from?.address,
  address_to: template.contact_to?.address,
  address_return: template.contact_return?.address,
  customer_info: {
    ...template.customer_info,
    address: template.customer_info?.contact?.address,
  },
  shipping_method: template.shipping_method?.id,
});

export function useShipmentTemplateRemoveMutation() {
  const queryKey = useShipmentTemplateQueryKey();
  const queryClient = useQueryClient();

  const mutationFn = UserProfileApiInstance.shipmentTemplatesDelete.userProfileDeleteShipmentTemplate;

  return useMutation({
    mutationFn: (id: string) => mutationFn({ id }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKey.lists(),
      });
    },
  });
}

export function useShipmentTemplateCreateMutation() {
  const queryKey = useShipmentTemplateQueryKey();
  const queryClient = useQueryClient();

  const mutationFn = UserProfileApiInstance.shipmentTemplatesCreate.userProfileCreateShipmentTemplate;

  return useMutation({
    mutationFn: (templateUpsert: TemplateUpsert) =>
      mutationFn({
        template: templateUpsertToAPIUpsert(templateUpsert),
        tag_ids: templateUpsert.tags?.map(t => t.id!),
      }),
    onSuccess: () => {
      void queryClient.refetchQueries({
        queryKey: queryKey.lists(),
      });
    },
  });
}

export function useShipmentTemplateUpdateMutation() {
  const queryKey = useShipmentTemplateQueryKey();
  const queryClient = useQueryClient();

  const mutationFn = UserProfileApiInstance.shipmentTemplatesUpdate.userProfileUpdateShipmentTemplate;

  return useMutation({
    mutationFn: (templateUpsert: TemplateUpsert) =>
      mutationFn({
        template: {
          data: templateUpsertToAPIUpsert(templateUpsert),
          id: templateUpsert.id,
        },
        tag_ids: templateUpsert.tags?.map(t => t.id!),
      }),
    onSuccess: ({ data: { template } }) => {
      void queryClient.invalidateQueries({ queryKey: queryKey.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKey.detail(template?.id) });
    },
  });
}
